import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { CampaignBreadcrumbs } from "@/components/CampaignBreadcrumbs";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageDropZone } from "@/components/ImageDropZone";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Edit, Trash2, Castle, Search, Building2, Users, ChevronDown, ChevronRight,
  ImageIcon, Globe2, FolderOpen, Upload, Flag, Wand2, Lock, Package, X, Target,
  FileText, Pin
} from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import AICreateMode from "@/components/AICreateMode";
import SearchableSelect from "@/components/SearchableSelect";
import EntityContextMenu from "@/components/EntityContextMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCATION_TYPES = ["city", "town", "village", "dungeon", "wilderness", "region", "landmark", "other"];
const BUILDING_TYPES = ["shop", "tavern", "temple", "guild", "residence", "castle", "barracks", "library", "other"];
const UNASSIGNED = "__unassigned__";
const ALIGNMENTS = ["Lawful Good","Neutral Good","Chaotic Good","Lawful Neutral","True Neutral","Chaotic Neutral","Lawful Evil","Neutral Evil","Chaotic Evil"];

const SECTIONS = [
  { key: "overview",  label: "Overview",  icon: FileText },
  { key: "buildings", label: "Buildings", icon: Building2 },
  { key: "npcs",      label: "NPCs",      icon: Users },
  { key: "items",     label: "Items",     icon: Package },
  { key: "factions",  label: "Factions",  icon: Flag },
  { key: "quests",    label: "Quests",    icon: Target },
];

const LocationsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  // Open a location as a full-page tab (over the whole page) via the
  // /campaign/:cid/locations/:locId route. The DocumentTabs system picks the
  // entity URL up automatically and adds a new tab. This is the click handler
  // the user explicitly asked for: 2 lists in the browser + a fresh full-page
  // tab when a location is chosen.
  const openLocationTab = (loc) => {
    if (!loc?.id) return;
    navigate(`/campaign/${campaignId}/locations/${loc.id}`);
  };
  const { user } = useAuth();
  const isCommoner = (user?.tier || "commoner") === "commoner";
  const confirm = useConfirm();

  // ===== Core data =====
  const [campaign, setCampaign] = useState(null);
  const [locations, setLocations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [maps, setMaps] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [factions, setFactions] = useState([]);
  const [items, setItems] = useState([]);
  const [quests, setQuests] = useState([]);
  const [unlinkedBuildings, setUnlinkedBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  // ===== Navigation state =====
  const [activeRegion, setActiveRegion] = useState(null); // region object or UNASSIGNED
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [expandedBuilding, setExpandedBuilding] = useState(null);

  // ===== UI state =====
  const [regionSearch, setRegionSearch] = useState("");
  const [locSearch, setLocSearch] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);

  // ===== Description autosave =====
  const [descDraft, setDescDraft] = useState("");
  const [descDirty, setDescDirty] = useState(false);
  const descTimer = useRef(null);

  // ===== Resizable layout =====
  // Two-pane: Regions | Locations. Clicking a location opens it as a full-page
  // tab via openLocationTab() so we no longer need a third detail panel here.
  const [panelSizes, setPanelSizes] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("np_locations_panels_v2"));
      if (Array.isArray(saved) && saved.length === 2) return saved;
    } catch { /* ignore */ }
    return [30, 70];
  });
  const onLayoutChange = (sizes) => {
    setPanelSizes(sizes);
    localStorage.setItem("np_locations_panels_v2", JSON.stringify(sizes));
  };

  // ===== Dialogs =====
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);
  const [npcDialogOpen, setNpcDialogOpen] = useState(false);
  const [factionDialogOpen, setFactionDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [editingRegion, setEditingRegion] = useState(null);
  const [editingNpc, setEditingNpc] = useState(null);
  const [editingFaction, setEditingFaction] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedNpc, setSelectedNpc] = useState(null);
  const [locCreateMode, setLocCreateMode] = useState("manual");
  const [bldgCreateMode, setBldgCreateMode] = useState("manual");
  const [npcCreateMode, setNpcCreateMode] = useState("manual");
  const [factionCreateMode, setFactionCreateMode] = useState("manual");
  const [itemCreateMode, setItemCreateMode] = useState("manual");

  // ===== Form data =====
  const [locForm, setLocForm] = useState({ name: "", description: "", location_type: "city", parent_location_id: "", map_id: "", region_id: "" });
  const [bldgForm, setBldgForm] = useState({ name: "", description: "", building_type: "shop", owner_character_id: "", menu_items: [] });
  const [regionForm, setRegionForm] = useState({ name: "", description: "" });
  const [npcForm, setNpcForm] = useState({ name: "", race: "", profession: "", personality: "", description: "" });
  const [factionForm, setFactionForm] = useState({ name: "", description: "", alignment: "", status: "active" });
  const [itemForm, setItemForm] = useState({ name: "", item_type: "misc", rarity: "common", description: "", properties: "" });

  // Pending images for dialogs
  const [pendingLocImage, setPendingLocImage] = useState(null);
  const [pendingBldgImage, setPendingBldgImage] = useState(null);
  const [pendingRegionImage, setPendingRegionImage] = useState(null);

  // ============ Data fetching ============
  const fetchData = useCallback(async () => {
    try {
      const [campRes, locRes, regRes, mapRes, charRes, npcRes, unlRes, facRes, itemRes, questRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/regions`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/maps`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/buildings/unlinked`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/factions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/items`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/quests`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCampaign(campRes.data);
      setLocations(locRes.data);
      setRegions(regRes.data);
      setMaps(mapRes.data);
      setCharacters(charRes.data);
      setNpcs(npcRes.data);
      setUnlinkedBuildings(unlRes.data);
      setFactions(facRes.data);
      setItems(itemRes.data);
      setQuests(questRes.data);
    } catch { toast.error("Failed to load locations"); }
    finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-select first region on initial load
  useEffect(() => {
    if (!loading && !activeRegion) {
      if (regions.length > 0) setActiveRegion(regions[0]);
      else if (locations.filter(l => !l.region_id).length > 0) setActiveRegion(UNASSIGNED);
    }
  }, [loading, regions, locations, activeRegion]);

  const fetchBuildings = useCallback(async (locationId) => {
    try {
      const res = await axios.get(`${API}/locations/${locationId}/buildings`, { withCredentials: true });
      setBuildings(res.data);
    } catch { setBuildings([]); }
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchBuildings(selectedLocation.id);
      setDescDraft(selectedLocation.description || "");
      setDescDirty(false);
      setActiveSection("overview");
      setExpandedBuilding(null);
    } else {
      setBuildings([]);
    }
  }, [selectedLocation?.id, fetchBuildings]);

  // Description autosave
  useEffect(() => {
    if (!descDirty || !selectedLocation) return;
    if (descTimer.current) clearTimeout(descTimer.current);
    descTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/locations/${selectedLocation.id}`, {
          campaign_id: campaignId,
          name: selectedLocation.name,
          description: descDraft,
          location_type: selectedLocation.location_type || "",
          parent_location_id: selectedLocation.parent_location_id || "",
          map_id: selectedLocation.map_id || null,
          region_id: selectedLocation.region_id || "",
          visibility: selectedLocation.visibility || "dm_only",
          visible_to: selectedLocation.visible_to || [],
        }, { withCredentials: true });
        setLocations(prev => prev.map(l => l.id === selectedLocation.id ? { ...l, description: descDraft } : l));
        setSelectedLocation(prev => prev ? { ...prev, description: descDraft } : prev);
        setDescDirty(false);
      } catch { toast.error("Failed to save description"); }
    }, 900);
    return () => { if (descTimer.current) clearTimeout(descTimer.current); };
  }, [descDraft, descDirty]); // eslint-disable-line

  // ============ Helpers ============
  const imgUrl = (path) => path ? (path.startsWith("http") ? path : `${API}/files/${path}`) : null;
  const isExternal = (path) => path && path.startsWith("http");
  const getCharName = (id) => {
    const c = characters.find(x => x.id === id);
    if (c) return c.name;
    const n = npcs.find(x => x.id === id);
    return n ? n.name : null;
  };
  const regionLocs = (rid) => locations.filter(l => l.region_id === rid);
  const unassignedLocs = locations.filter(l => !l.region_id);

  // ============ Region CRUD ============
  const handleRegionSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...regionForm, campaign_id: campaignId };
      let rid;
      if (editingRegion) {
        await axios.put(`${API}/regions/${editingRegion.id}`, payload, { withCredentials: true });
        rid = editingRegion.id;
        toast.success("Region updated");
      } else {
        const res = await axios.post(`${API}/regions`, payload, { withCredentials: true });
        rid = res.data.id;
        toast.success("Region created");
      }
      if (pendingRegionImage && rid) {
        const fd = new FormData();
        fd.append("file", pendingRegionImage);
        await axios.post(`${API}/regions/${rid}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      }
      setRegionDialogOpen(false);
      resetRegionForm();
      await fetchData();
    } catch { toast.error("Failed to save region"); }
  };

  const handleDeleteRegion = async (id, e) => {
    e?.stopPropagation();
    const ok = await confirm("Delete Region", "Locations in this region will become unassigned. Continue?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/regions/${id}`, { withCredentials: true });
      toast.success("Region deleted");
      if (activeRegion && activeRegion !== UNASSIGNED && activeRegion.id === id) {
        setActiveRegion(null);
        setSelectedLocation(null);
      }
      fetchData();
    } catch { toast.error("Failed to delete region"); }
  };

  const resetRegionForm = () => { setEditingRegion(null); setRegionForm({ name: "", description: "" }); setPendingRegionImage(null); };
  const openCreateRegion = () => { resetRegionForm(); setRegionDialogOpen(true); };
  const openEditRegion = (region, e) => {
    e?.stopPropagation();
    setEditingRegion(region);
    setRegionForm({ name: region.name, description: region.description || "" });
    setPendingRegionImage(null);
    setRegionDialogOpen(true);
  };

  // ============ Location CRUD ============
  const handleLocSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...locForm, campaign_id: campaignId };
      if (activeRegion && activeRegion !== UNASSIGNED && !payload.region_id) {
        payload.region_id = activeRegion.id;
      }
      let lid;
      if (editing) {
        await axios.put(`${API}/locations/${editing.id}`, payload, { withCredentials: true });
        lid = editing.id;
        toast.success("Location updated");
      } else {
        const res = await axios.post(`${API}/locations`, payload, { withCredentials: true });
        lid = res.data.id;
        toast.success("Location created");
      }
      if (pendingLocImage && lid) {
        const fd = new FormData();
        fd.append("file", pendingLocImage);
        await axios.post(`${API}/locations/${lid}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      }
      const up = await axios.get(`${API}/locations/${lid}`, { withCredentials: true });
      setSelectedLocation(up.data);
      setLocationDialogOpen(false);
      resetLocForm();
      fetchData();
    } catch { toast.error("Failed to save location"); }
  };

  const handleDeleteLoc = async (id) => {
    const ok = await confirm("Delete Location", "Delete this location and all its buildings?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/locations/${id}`, { withCredentials: true });
      toast.success("Location deleted");
      if (selectedLocation?.id === id) setSelectedLocation(null);
      fetchData();
    } catch { toast.error("Failed to delete location"); }
  };

  const handleLocImageUpload = async (lid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploadingImage(true);
    try {
      await axios.post(`${API}/locations/${lid}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      const r = await axios.get(`${API}/locations/${lid}`, { withCredentials: true });
      setSelectedLocation(r.data);
      fetchData();
    } catch { toast.error("Failed to upload image"); }
    finally { setUploadingImage(false); }
  };

  const resetLocForm = () => {
    setEditing(null);
    setLocForm({
      name: "", description: "", location_type: "city", parent_location_id: "", map_id: "",
      region_id: (activeRegion && activeRegion !== UNASSIGNED) ? activeRegion.id : ""
    });
    setPendingLocImage(null);
  };

  const openCreateLoc = () => { resetLocForm(); setLocCreateMode("manual"); setLocationDialogOpen(true); };
  const openEditLoc = (loc) => {
    setEditing(loc);
    setLocForm({
      name: loc.name, description: loc.description || "", location_type: loc.location_type || "city",
      parent_location_id: loc.parent_location_id || "", map_id: loc.map_id || "", region_id: loc.region_id || ""
    });
    setPendingLocImage(null);
    setLocationDialogOpen(true);
  };

  // ============ Building CRUD ============
  const handleBldgSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLocation) return;
    try {
      const payload = { ...bldgForm, location_id: selectedLocation.id, campaign_id: campaignId, menu_items: bldgForm.menu_items?.filter(m => m.name.trim()) || [] };
      let bid;
      if (editingBuilding) {
        await axios.put(`${API}/buildings/${editingBuilding.id}`, payload, { withCredentials: true });
        bid = editingBuilding.id;
        toast.success("Building updated");
      } else {
        const res = await axios.post(`${API}/buildings`, payload, { withCredentials: true });
        bid = res.data.id;
        toast.success("Building created");
      }
      if (pendingBldgImage && bid) {
        const fd = new FormData();
        fd.append("file", pendingBldgImage);
        await axios.post(`${API}/buildings/${bid}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      }
      setBuildingDialogOpen(false);
      resetBldgForm();
      fetchBuildings(selectedLocation.id);
    } catch { toast.error("Failed to save building"); }
  };

  const handleDeleteBldg = async (id) => {
    const ok = await confirm("Delete Building", "Are you sure you want to delete this building?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/buildings/${id}`, { withCredentials: true });
      toast.success("Building deleted");
      fetchBuildings(selectedLocation.id);
    } catch { toast.error("Failed to delete building"); }
  };

  const handleBldgImageUpload = async (bid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploadingImage(true);
    try {
      await axios.post(`${API}/buildings/${bid}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Building image uploaded");
      if (selectedLocation) fetchBuildings(selectedLocation.id);
    } catch { toast.error("Failed to upload image"); }
    finally { setUploadingImage(false); }
  };

  const resetBldgForm = () => { setEditingBuilding(null); setBldgForm({ name: "", description: "", building_type: "shop", owner_character_id: "", menu_items: [] }); setPendingBldgImage(null); };
  const openCreateBldg = () => { resetBldgForm(); setBldgCreateMode("manual"); setBuildingDialogOpen(true); };
  const openEditBldg = (b) => {
    setEditingBuilding(b);
    setBldgForm({ name: b.name, description: b.description || "", building_type: b.building_type || "shop", owner_character_id: b.owner_character_id || "", menu_items: b.menu_items || [] });
    setPendingBldgImage(null);
    setBuildingDialogOpen(true);
  };

  // ============ NPC CRUD ============
  const resetNpcForm = () => { setEditingNpc(null); setNpcForm({ name: "", race: "", profession: "", personality: "", description: "" }); };
  const openCreateNpc = () => { resetNpcForm(); setNpcCreateMode("manual"); setNpcDialogOpen(true); };
  const openEditNpc = (npc) => {
    setEditingNpc(npc);
    setNpcForm({ name: npc.name, race: npc.race || "", profession: npc.profession || "", personality: npc.personality || "", description: npc.description || "" });
    setNpcDialogOpen(true);
  };
  const handleNpcSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...npcForm, campaign_id: campaignId, location_id: selectedLocation.id };
      if (editingNpc) {
        await axios.put(`${API}/npcs/${editingNpc.id}`, payload, { withCredentials: true });
        toast.success("NPC updated");
      } else {
        await axios.post(`${API}/npcs`, payload, { withCredentials: true });
        toast.success("NPC created");
      }
      setNpcDialogOpen(false);
      resetNpcForm();
      fetchData();
    } catch { toast.error("Failed to save NPC"); }
  };
  const handleDeleteNpc = async (id) => {
    const ok = await confirm("Delete NPC", "Are you sure?");
    if (!ok) return;
    try { await axios.delete(`${API}/npcs/${id}`, { withCredentials: true }); toast.success("NPC deleted"); fetchData(); }
    catch { toast.error("Failed to delete NPC"); }
  };

  // ============ Faction CRUD ============
  const resetFactionForm = () => { setEditingFaction(null); setFactionForm({ name: "", description: "", alignment: "", status: "active" }); };
  const openCreateFaction = () => { resetFactionForm(); setFactionCreateMode("manual"); setFactionDialogOpen(true); };
  const openEditFaction = (f) => {
    setEditingFaction(f);
    setFactionForm({ name: f.name, description: f.description || "", alignment: f.alignment || "", status: f.status || "active" });
    setFactionDialogOpen(true);
  };
  const handleFactionSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...factionForm, location_id: selectedLocation.id };
      if (editingFaction) {
        await axios.put(`${API}/factions/${editingFaction.id}`, payload, { withCredentials: true });
        toast.success("Faction updated");
      } else {
        await axios.post(`${API}/campaigns/${campaignId}/factions`, payload, { withCredentials: true });
        toast.success("Faction created");
      }
      setFactionDialogOpen(false);
      resetFactionForm();
      fetchData();
    } catch { toast.error("Failed to save faction"); }
  };
  const handleDeleteFaction = async (id) => {
    const ok = await confirm("Delete Faction", "Are you sure?");
    if (!ok) return;
    try { await axios.delete(`${API}/factions/${id}`, { withCredentials: true }); toast.success("Faction deleted"); fetchData(); }
    catch { toast.error("Failed to delete faction"); }
  };

  // ============ Item CRUD ============
  const resetItemForm = () => { setEditingItem(null); setItemForm({ name: "", item_type: "misc", rarity: "common", description: "", properties: "" }); };
  const openCreateItem = () => { resetItemForm(); setItemCreateMode("manual"); setItemDialogOpen(true); };
  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({ name: item.name, item_type: item.item_type || "misc", rarity: item.rarity || "common", description: item.description || "", properties: item.properties || "" });
    setItemDialogOpen(true);
  };
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...itemForm, campaign_id: campaignId, location_id: selectedLocation.id };
      if (editingItem) {
        await axios.put(`${API}/items/${editingItem.id}`, payload, { withCredentials: true });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/items`, payload, { withCredentials: true });
        toast.success("Item created");
      }
      setItemDialogOpen(false);
      resetItemForm();
      fetchData();
    } catch { toast.error("Failed to save item"); }
  };
  const handleDeleteItem = async (id) => {
    const ok = await confirm("Delete Item", "Are you sure?");
    if (!ok) return;
    try { await axios.delete(`${API}/items/${id}`, { withCredentials: true }); toast.success("Item deleted"); fetchData(); }
    catch { toast.error("Failed to delete item"); }
  };

  // ============ Pin to sidebar ============
  const pinRegion = (region, e) => {
    e?.stopPropagation();
    try {
      const key = `np_pinned_${campaignId}`;
      const cur = JSON.parse(localStorage.getItem(key)) || [];
      const entry = { path: "locations", label: region.name };
      const exists = cur.some(p => p.label === region.name);
      const next = exists ? cur.filter(p => p.label !== region.name) : [entry, ...cur].slice(0, 6);
      localStorage.setItem(key, JSON.stringify(next));
      window.dispatchEvent(new Event("np-pinned-changed"));
      toast.success(exists ? "Unpinned" : "Pinned to sidebar");
    } catch {}
  };

  // ============ Derived ============
  const filteredRegions = regions.filter(r => r.name.toLowerCase().includes(regionSearch.toLowerCase()));
  const showUnassignedEntry = unassignedLocs.length > 0 && "unassigned".includes(regionSearch.toLowerCase());
  const isUnassigned = activeRegion === UNASSIGNED;
  const viewLocs = isUnassigned ? unassignedLocs : (activeRegion ? regionLocs(activeRegion.id) : []);
  const displayLocs = viewLocs.filter(l => l.name.toLowerCase().includes(locSearch.toLowerCase()));

  const locNpcs = selectedLocation ? npcs.filter(n => n.location_id === selectedLocation.id) : [];
  const locChars = selectedLocation ? characters.filter(c => c.location_id === selectedLocation.id) : [];
  const locFactions = selectedLocation ? factions.filter(f => f.location_id === selectedLocation.id) : [];
  const locItems = selectedLocation ? items.filter(i => i.location_id === selectedLocation.id) : [];
  const locQuests = selectedLocation ? quests.filter(q => q.location_id === selectedLocation.id) : [];

  const counts = {
    overview: null,
    buildings: buildings.length,
    npcs: locNpcs.length + locChars.length,
    items: locItems.length,
    factions: locFactions.length,
    quests: locQuests.length,
  };

  const extraCrumbs = [];
  if (activeRegion && activeRegion !== UNASSIGNED) extraCrumbs.push({ label: activeRegion.name });
  if (isUnassigned) extraCrumbs.push({ label: "Unassigned" });
  if (selectedLocation) extraCrumbs.push({ label: selectedLocation.name });

  if (loading) {
    return (
      <Layout campaignName="Loading..." hideBreadcrumbs>
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      </Layout>
    );
  }

  // ============ Image Picker (reusable) ============
  const ImagePicker = ({ pending, onPick, onClear, testId }) => (
    <div>
      <label className="text-sm font-medium text-foreground block mb-2">Image</label>
      <ImageDropZone
        compact
        onFileSelect={onPick}
        preview={pending ? URL.createObjectURL(pending) : null}
        fileName={pending?.name}
        testId={testId}
        onClear={onClear}
      />
    </div>
  );

  const locThumb = (loc) => imgUrl(loc.image_path);
  const regionThumb = (r) => imgUrl(r.image_path);

  // ============================================================
  return (
    <Layout campaignName={campaign?.name} hideBreadcrumbs>
      <CampaignBreadcrumbs campaignName={campaign?.name} extraCrumbs={extraCrumbs} />

      <div className="rounded-sm border border-border bg-card overflow-hidden" style={{ height: "calc(100vh - 200px)", minHeight: 600 }} data-testid="world-notebook">
        <ResizablePanelGroup direction="horizontal" onLayout={onLayoutChange}>

          {/* ============== LEFT — REGIONS ============== */}
          <ResizablePanel defaultSize={panelSizes[0]} minSize={20} maxSize={45}>
            <div className="h-full flex flex-col bg-[#110d0a]">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <Globe2 className="w-3.5 h-3.5 text-primary" />
                <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80 flex-1">Regions</h3>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={openCreateRegion} title="New Region" data-testid="add-region-btn">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="p-2 border-b border-border/40">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground/60" />
                  <Input value={regionSearch} onChange={e => setRegionSearch(e.target.value)} placeholder="Search regions…" className="h-7 text-xs pl-7" data-testid="region-search" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {filteredRegions.length === 0 && !showUnassignedEntry && (
                  <div className="text-center py-10 px-3">
                    <Globe2 className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground/70 mb-3">No regions yet. Organize your world into regions.</p>
                    <Button size="sm" onClick={openCreateRegion} className="text-xs h-7" data-testid="add-first-region-btn">
                      <Plus className="w-3 h-3 mr-1" /> New Region
                    </Button>
                  </div>
                )}
                {filteredRegions.map((r) => {
                  const active = activeRegion && activeRegion !== UNASSIGNED && activeRegion.id === r.id;
                  const count = regionLocs(r.id).length;
                  const thumb = regionThumb(r);
                  return (
                    <div key={r.id} className="group relative">
                      <button
                        onClick={() => { setActiveRegion(r); setSelectedLocation(null); setLocSearch(""); }}
                        className={`w-full text-left flex items-center gap-2 px-2 py-2 rounded-sm transition-colors ${active ? "bg-primary/15 text-foreground" : "hover:bg-muted/30 text-muted-foreground"}`}
                        data-testid={`region-list-item-${r.id}`}
                      >
                        {thumb ? (
                          <img src={thumb} alt={r.name} className="w-7 h-7 rounded-sm object-cover flex-shrink-0" {...(!isExternal(r.image_path) && { crossOrigin: "use-credentials" })} />
                        ) : (
                          <div className="w-7 h-7 rounded-sm bg-muted/30 flex items-center justify-center flex-shrink-0">
                            <Globe2 className={`w-3.5 h-3.5 ${active ? "text-primary" : "text-muted-foreground/60"}`} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{r.name}</p>
                          <p className="text-[10px] text-muted-foreground/60">{count} location{count !== 1 ? "s" : ""}</p>
                        </div>
                      </button>
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                        <button onClick={(e) => pinRegion(r, e)} className="p-1 text-muted-foreground/60 hover:text-primary" title="Pin" data-testid={`pin-region-${r.id}`}>
                          <Pin className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => openEditRegion(r, e)} className="p-1 text-muted-foreground/60 hover:text-foreground" title="Edit" data-testid={`edit-region-${r.id}`}>
                          <Edit className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => handleDeleteRegion(r.id, e)} className="p-1 text-muted-foreground/60 hover:text-destructive" title="Delete" data-testid={`delete-region-${r.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {showUnassignedEntry && (
                  <button
                    onClick={() => { setActiveRegion(UNASSIGNED); setSelectedLocation(null); setLocSearch(""); }}
                    className={`w-full text-left flex items-center gap-2 px-2 py-2 rounded-sm transition-colors mt-2 border-t border-border/30 pt-3 ${isUnassigned ? "bg-primary/15 text-foreground" : "hover:bg-muted/30 text-muted-foreground"}`}
                    data-testid="unassigned-region-item"
                  >
                    <div className="w-7 h-7 rounded-sm bg-muted/30 flex items-center justify-center flex-shrink-0 border border-dashed border-border/60">
                      <FolderOpen className="w-3.5 h-3.5 text-muted-foreground/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">Unassigned</p>
                      <p className="text-[10px] text-muted-foreground/60">{unassignedLocs.length} location{unassignedLocs.length !== 1 ? "s" : ""}</p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* ============== MIDDLE — LOCATIONS IN REGION ============== */}
          <ResizablePanel defaultSize={panelSizes[1]} minSize={55}>
            <div className="h-full flex flex-col bg-[#14100c]">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <Castle className="w-3.5 h-3.5 text-primary/80" />
                <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80 flex-1 truncate" data-testid="middle-region-header">
                  {activeRegion ? (isUnassigned ? "Unassigned" : activeRegion.name) : "Locations"}
                </h3>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={openCreateLoc} disabled={!activeRegion} title="New Location" data-testid="add-location-btn">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              {activeRegion && (
                <div className="p-2 border-b border-border/40">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground/60" />
                    <Input value={locSearch} onChange={e => setLocSearch(e.target.value)} placeholder="Search locations…" className="h-7 text-xs pl-7" data-testid="location-search" />
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-2">
                {!activeRegion && (
                  <p className="text-xs text-muted-foreground/60 italic text-center py-10 px-3">Pick a region to view its locations.</p>
                )}
                {activeRegion && displayLocs.length === 0 && (
                  <div className="text-center py-10 px-3">
                    <Castle className="w-7 h-7 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground/70 mb-3">
                      {isUnassigned ? "No unassigned locations." : "No locations in this region yet."}
                    </p>
                    {!isUnassigned && (
                      <Button size="sm" onClick={openCreateLoc} className="text-xs h-7" data-testid="add-first-location-btn">
                        <Plus className="w-3 h-3 mr-1" /> New Location
                      </Button>
                    )}
                  </div>
                )}
                {displayLocs.map((loc) => {
                  const active = selectedLocation?.id === loc.id;
                  const thumb = locThumb(loc);
                  return (
                    <EntityContextMenu
                      key={loc.id}
                      onOpen={() => openLocationTab(loc)}
                      onRename={() => openEditLoc(loc)}
                      onDelete={() => handleDeleteLoc(loc.id)}
                      pinPath="locations"
                      pinLabel={`Location: ${loc.name}`}
                      entityName={loc.name}
                    >
                    <div className="group relative mb-0.5">
                      <button
                        onClick={() => openLocationTab(loc)}
                        className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors ${active ? "bg-primary/15 text-foreground" : "hover:bg-muted/30 text-muted-foreground"}`}
                        data-testid={`location-list-item-${loc.id}`}
                      >
                        {thumb ? (
                          <img src={thumb} alt={loc.name} className="w-9 h-9 rounded-sm object-cover flex-shrink-0" {...(!isExternal(loc.image_path) && { crossOrigin: "use-credentials" })} />
                        ) : (
                          <div className="w-9 h-9 rounded-sm bg-muted/30 flex items-center justify-center flex-shrink-0">
                            <Castle className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground/60"}`} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate font-medium">{loc.name}</p>
                          <p className="text-[10px] text-muted-foreground/60 capitalize">{loc.location_type}</p>
                        </div>
                        <ChevronRight className={`w-3 h-3 flex-shrink-0 ${active ? "text-primary" : "text-muted-foreground/40"}`} />
                      </button>
                    </div>
                    </EntityContextMenu>
                  );
                })}

                {/* Unlinked buildings — visible only when no region selected or in Unassigned */}
                {(isUnassigned || activeRegion === regions[0]) && unlinkedBuildings.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/30">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-2 px-2 flex items-center gap-1.5">
                      <Building2 className="w-3 h-3" /> Unlinked Buildings ({unlinkedBuildings.length})
                    </p>
                    {unlinkedBuildings.map(b => (
                      <div key={b.id} className="px-2 py-1.5 mb-1 rounded-sm bg-muted/10 border border-border/30" data-testid={`unlinked-building-row-${b.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-foreground truncate">{b.name}</p>
                            <p className="text-[10px] text-muted-foreground/60 capitalize">{b.building_type}</p>
                          </div>
                          <Select onValueChange={async (locId) => {
                            try {
                              await axios.put(`${API}/buildings/${b.id}`, { ...b, location_id: locId, campaign_id: campaignId }, { withCredentials: true });
                              toast.success(`${b.name} linked`);
                              fetchData();
                              if (selectedLocation?.id === locId) fetchBuildings(locId);
                            } catch { toast.error("Failed to link"); }
                          }}>
                            <SelectTrigger className="h-6 w-24 text-[10px] bg-card border-border" data-testid={`link-building-${b.id}`}>
                              <SelectValue placeholder="Link…" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border-border">
                              {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ResizablePanel>

        </ResizablePanelGroup>
      </div>

      {/* ============ NPC DETAIL DRAWER ============ */}
      {selectedNpc && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelectedNpc(null)}>
          <div className="w-full max-w-md bg-card border-l border-border h-full overflow-y-auto animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()} data-testid="npc-detail-panel">
            <div className="sticky top-0 bg-card z-10 p-4 border-b border-border flex justify-between items-center">
              <h3 className="text-lg font-serif text-foreground">{selectedNpc.name}</h3>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { openEditNpc(selectedNpc); setSelectedNpc(null); }}><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedNpc(null)}><X className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {selectedNpc.image_url && (
                <img src={imgUrl(selectedNpc.image_url)} alt={selectedNpc.name} className="w-full rounded-sm object-cover max-h-48" {...(!isExternal(selectedNpc.image_url) && { crossOrigin: "use-credentials" })} />
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selectedNpc.race && <div><span className="text-muted-foreground text-xs block">Race</span><span className="text-foreground">{selectedNpc.race}</span></div>}
                {selectedNpc.profession && <div><span className="text-muted-foreground text-xs block">Profession</span><span className="text-foreground">{selectedNpc.profession}</span></div>}
                {selectedNpc.personality && <div className="col-span-2"><span className="text-muted-foreground text-xs block">Personality</span><span className="text-foreground italic">{selectedNpc.personality}</span></div>}
              </div>
              {selectedNpc.description && (
                <div className="border-t border-border pt-3">
                  <span className="text-muted-foreground text-xs block mb-1">Description</span>
                  <div className="rich-content prose prose-invert max-w-none text-sm" dangerouslySetInnerHTML={{ __html: selectedNpc.description }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ REGION DIALOG ============ */}
      <Dialog open={regionDialogOpen} onOpenChange={setRegionDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingRegion ? "Edit Region" : "New Region"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegionSubmit} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Name</label>
              <Input value={regionForm.name} onChange={(e) => setRegionForm({ ...regionForm, name: e.target.value })} placeholder="The Western Reach" required data-testid="region-name-input" className="bg-input border-border rounded-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Description</label>
              <Input value={regionForm.description} onChange={(e) => setRegionForm({ ...regionForm, description: e.target.value })} placeholder="A coastal region spanning..." data-testid="region-description-input" className="bg-input border-border rounded-sm" />
            </div>
            <ImagePicker pending={pendingRegionImage} onPick={setPendingRegionImage} onClear={() => setPendingRegionImage(null)} testId="region-image-input" />
            <Button type="submit" data-testid="save-region-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              {editingRegion ? "Save Changes" : "Create Region"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ LOCATION DIALOG ============ */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Location" : "New Location"}</DialogTitle>
          </DialogHeader>
          {!editing && (
            <div className="flex gap-2 border-b border-border pb-3 mb-1 flex-shrink-0">
              <Button variant={locCreateMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setLocCreateMode("manual")} data-testid="mode-manual">
                <Plus className="w-3 h-3 mr-1" /> Create Manually
              </Button>
              {!isCommoner && (
                <Button variant={locCreateMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setLocCreateMode("ai")} data-testid="mode-ai">
                  <Wand2 className="w-3 h-3 mr-1" /> Use AI
                </Button>
              )}
              {isCommoner && (
                <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled>
                  <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                </Button>
              )}
            </div>
          )}
          {!editing && locCreateMode === "ai" && (
            <AICreateMode entityType="location" campaignId={campaignId} onGenerated={(data) => {
              setLocForm(prev => ({ ...prev, ...data }));
              setLocCreateMode("manual");
            }} />
          )}
          <form onSubmit={handleLocSubmit} className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2" style={{ display: (!editing && locCreateMode === "ai") ? "none" : undefined }}>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Name</label>
              <Input value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} placeholder="Waterdeep" required data-testid="location-name-input" className="bg-input border-border rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Type</label>
                <Select value={locForm.location_type} onValueChange={(v) => setLocForm({ ...locForm, location_type: v })}>
                  <SelectTrigger className="bg-input border-border rounded-sm" data-testid="location-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {LOCATION_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Region</label>
                <Select value={locForm.region_id || "__none__"} onValueChange={(v) => setLocForm({ ...locForm, region_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="bg-input border-border rounded-sm" data-testid="location-region-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="__none__">None</SelectItem>
                    {regions.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Parent Location</label>
                <SearchableSelect
                  value={locForm.parent_location_id}
                  onChange={(v) => setLocForm({ ...locForm, parent_location_id: v })}
                  options={locations.filter(l => l.id !== editing?.id).map(l => ({ value: l.id, label: l.name }))}
                  placeholder="None (top-level)"
                  testId="parent-location-select"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Associated Map</label>
                <SearchableSelect
                  value={locForm.map_id}
                  onChange={(v) => setLocForm({ ...locForm, map_id: v })}
                  options={maps.map(m => ({ value: m.id, label: m.name }))}
                  placeholder="None"
                  testId="location-map-select"
                />
              </div>
            </div>
            <ImagePicker pending={pendingLocImage} onPick={setPendingLocImage} onClear={() => setPendingLocImage(null)} testId="location-image-input" />
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Description</label>
              <RichTextEditor value={locForm.description} onChange={(v) => setLocForm({ ...locForm, description: v })} placeholder="Describe this location..." />
            </div>
            <Button type="submit" data-testid="save-location-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              {editing ? "Save Changes" : "Create Location"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ BUILDING DIALOG ============ */}
      <Dialog open={buildingDialogOpen} onOpenChange={setBuildingDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-serif text-xl">{editingBuilding ? "Edit Building" : "New Building"}</DialogTitle>
          </DialogHeader>
          {!editingBuilding && (
            <div className="flex gap-2 border-b border-border pb-3 mb-1 flex-shrink-0">
              <Button type="button" variant={bldgCreateMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setBldgCreateMode("manual")} data-testid="bldg-mode-manual">
                <Plus className="w-3 h-3 mr-1" /> Create Manually
              </Button>
              {!isCommoner && (
                <Button type="button" variant={bldgCreateMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setBldgCreateMode("ai")} data-testid="bldg-mode-ai">
                  <Wand2 className="w-3 h-3 mr-1" /> Use AI
                </Button>
              )}
              {isCommoner && (
                <Button type="button" variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled>
                  <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                </Button>
              )}
            </div>
          )}
          {!editingBuilding && bldgCreateMode === "ai" && (
            <AICreateMode entityType="building" campaignId={campaignId} onGenerated={(data) => {
              setBldgForm(prev => ({ ...prev, ...data }));
              setBldgCreateMode("manual");
            }} />
          )}
          <form onSubmit={handleBldgSubmit} className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2" style={{ display: (!editingBuilding && bldgCreateMode === "ai") ? "none" : undefined }}>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Name</label>
              <Input value={bldgForm.name} onChange={(e) => setBldgForm({ ...bldgForm, name: e.target.value })} placeholder="The Yawning Portal" required data-testid="building-name-input" className="bg-input border-border rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Type</label>
                <Select value={bldgForm.building_type} onValueChange={(v) => setBldgForm({ ...bldgForm, building_type: v })}>
                  <SelectTrigger className="bg-input border-border rounded-sm" data-testid="building-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {BUILDING_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Owner</label>
                <SearchableSelect
                  value={bldgForm.owner_character_id}
                  onChange={(v) => setBldgForm({ ...bldgForm, owner_character_id: v })}
                  options={[...characters, ...npcs].map(c => ({ value: c.id, label: c.name }))}
                  placeholder="Select owner..."
                  testId="building-owner-select"
                />
              </div>
            </div>
            <ImagePicker pending={pendingBldgImage} onPick={setPendingBldgImage} onClear={() => setPendingBldgImage(null)} testId="building-image-input" />
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Description</label>
              <RichTextEditor value={bldgForm.description} onChange={(v) => setBldgForm({ ...bldgForm, description: v })} placeholder="Describe this building..." />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Menu / Price List</label>
              <p className="text-xs text-muted-foreground mb-2">Add items and prices for taverns, shops, etc.</p>
              <div className="space-y-2">
                {(bldgForm.menu_items || []).map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input value={item.name} onChange={(e) => {
                      const updated = [...bldgForm.menu_items];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setBldgForm({ ...bldgForm, menu_items: updated });
                    }} placeholder="Ale, Sword, Healing Potion..." className="bg-input border-border rounded-sm flex-1 h-8 text-sm" data-testid={`menu-item-name-${idx}`} />
                    <Input value={item.price} onChange={(e) => {
                      const updated = [...bldgForm.menu_items];
                      updated[idx] = { ...updated[idx], price: e.target.value };
                      setBldgForm({ ...bldgForm, menu_items: updated });
                    }} placeholder="5 gp" className="bg-input border-border rounded-sm w-24 h-8 text-sm" data-testid={`menu-item-price-${idx}`} />
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive flex-shrink-0" onClick={() => {
                      setBldgForm({ ...bldgForm, menu_items: bldgForm.menu_items.filter((_, i) => i !== idx) });
                    }} data-testid={`remove-menu-item-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="border-border rounded-sm text-xs" onClick={() => {
                  setBldgForm({ ...bldgForm, menu_items: [...(bldgForm.menu_items || []), { name: "", price: "" }] });
                }} data-testid="add-menu-item">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
            </div>
            <Button type="submit" data-testid="save-building-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              {editingBuilding ? "Save Changes" : "Create Building"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ NPC DIALOG ============ */}
      <Dialog open={npcDialogOpen} onOpenChange={setNpcDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-serif text-xl">{editingNpc ? "Edit NPC" : "New NPC"}</DialogTitle>
          </DialogHeader>
          {!editingNpc && (
            <div className="flex gap-2 border-b border-border pb-3 mb-1 flex-shrink-0">
              <Button variant={npcCreateMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setNpcCreateMode("manual")} data-testid="npc-mode-manual">
                <Plus className="w-3 h-3 mr-1" /> Create Manually
              </Button>
              {!isCommoner && (
                <Button variant={npcCreateMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setNpcCreateMode("ai")} data-testid="npc-mode-ai">
                  <Wand2 className="w-3 h-3 mr-1" /> Use AI
                </Button>
              )}
              {isCommoner && (
                <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled>
                  <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                </Button>
              )}
            </div>
          )}
          {!editingNpc && npcCreateMode === "ai" && (
            <AICreateMode entityType="npc" campaignId={campaignId} onGenerated={(data) => {
              setNpcForm(prev => ({ ...prev, ...data }));
              setNpcCreateMode("manual");
            }} />
          )}
          <form onSubmit={handleNpcSubmit} className="space-y-3 mt-2 overflow-y-auto flex-1 pr-1" style={{ display: (!editingNpc && npcCreateMode === "ai") ? "none" : undefined }}>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Name *</label>
              <Input value={npcForm.name} onChange={(e) => setNpcForm({ ...npcForm, name: e.target.value })} placeholder="Thorin Ironforge" required data-testid="npc-name-input" className="bg-input border-border rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Race</label>
                <Input value={npcForm.race} onChange={(e) => setNpcForm({ ...npcForm, race: e.target.value })} placeholder="Dwarf" data-testid="npc-race-input" className="bg-input border-border rounded-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Profession</label>
                <Input value={npcForm.profession} onChange={(e) => setNpcForm({ ...npcForm, profession: e.target.value })} placeholder="Blacksmith" data-testid="npc-profession-input" className="bg-input border-border rounded-sm" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Personality</label>
              <Input value={npcForm.personality} onChange={(e) => setNpcForm({ ...npcForm, personality: e.target.value })} placeholder="Gruff but generous" data-testid="npc-personality-input" className="bg-input border-border rounded-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Description</label>
              <RichTextEditor value={npcForm.description} onChange={(v) => setNpcForm({ ...npcForm, description: v })} placeholder="Describe this NPC..." />
            </div>
            <Button type="submit" data-testid="save-npc-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              {editingNpc ? "Save Changes" : "Create NPC"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ FACTION DIALOG ============ */}
      <Dialog open={factionDialogOpen} onOpenChange={setFactionDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-serif text-xl">{editingFaction ? "Edit Faction" : "New Faction"}</DialogTitle>
          </DialogHeader>
          {!editingFaction && (
            <div className="flex gap-2 border-b border-border pb-3 mb-1 flex-shrink-0">
              <Button variant={factionCreateMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setFactionCreateMode("manual")} data-testid="faction-mode-manual">
                <Plus className="w-3 h-3 mr-1" /> Create Manually
              </Button>
              {!isCommoner && (
                <Button variant={factionCreateMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setFactionCreateMode("ai")} data-testid="faction-mode-ai">
                  <Wand2 className="w-3 h-3 mr-1" /> Use AI
                </Button>
              )}
              {isCommoner && (
                <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled>
                  <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                </Button>
              )}
            </div>
          )}
          {!editingFaction && factionCreateMode === "ai" && (
            <AICreateMode entityType="lore" campaignId={campaignId} onGenerated={(data) => {
              setFactionForm(prev => ({ ...prev, name: data.title || data.name || "", description: data.content || data.description || "", alignment: data.alignment || "" }));
              setFactionCreateMode("manual");
            }} />
          )}
          <form onSubmit={handleFactionSubmit} className="space-y-3 mt-2 overflow-y-auto flex-1 pr-1" style={{ display: (!editingFaction && factionCreateMode === "ai") ? "none" : undefined }}>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Name *</label>
              <Input value={factionForm.name} onChange={(e) => setFactionForm({ ...factionForm, name: e.target.value })} placeholder="The Order of the Silver Shield" required data-testid="faction-name-input" className="bg-input border-border rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Alignment</label>
                <Select value={factionForm.alignment} onValueChange={(v) => setFactionForm({ ...factionForm, alignment: v })}>
                  <SelectTrigger className="bg-input border-border rounded-sm" data-testid="faction-alignment-select"><SelectValue placeholder="Select alignment" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {ALIGNMENTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Status</label>
                <Select value={factionForm.status} onValueChange={(v) => setFactionForm({ ...factionForm, status: v })}>
                  <SelectTrigger className="bg-input border-border rounded-sm" data-testid="faction-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {["active","disbanded","secret","at war","allied"].map(s => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Description</label>
              <RichTextEditor value={factionForm.description} onChange={(v) => setFactionForm({ ...factionForm, description: v })} placeholder="Describe this faction..." />
            </div>
            <Button type="submit" data-testid="save-faction-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              {editingFaction ? "Save Changes" : "Create Faction"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ ITEM DIALOG ============ */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-serif text-xl">{editingItem ? "Edit Item" : "New Item"}</DialogTitle>
          </DialogHeader>
          {!editingItem && (
            <div className="flex gap-2 border-b border-border pb-3 mb-1 flex-shrink-0">
              <Button variant={itemCreateMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setItemCreateMode("manual")} data-testid="item-mode-manual">
                <Plus className="w-3 h-3 mr-1" /> Create Manually
              </Button>
              {!isCommoner && (
                <Button variant={itemCreateMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setItemCreateMode("ai")} data-testid="item-mode-ai">
                  <Wand2 className="w-3 h-3 mr-1" /> Use AI
                </Button>
              )}
              {isCommoner && (
                <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled>
                  <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                </Button>
              )}
            </div>
          )}
          {!editingItem && itemCreateMode === "ai" && (
            <AICreateMode entityType="item" campaignId={campaignId} onGenerated={(data) => {
              setItemForm(prev => ({ ...prev, ...data }));
              setItemCreateMode("manual");
            }} />
          )}
          <form onSubmit={handleItemSubmit} className="space-y-3 mt-2 overflow-y-auto flex-1 pr-1" style={{ display: (!editingItem && itemCreateMode === "ai") ? "none" : undefined }}>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Name *</label>
              <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="Flame Tongue Longsword" required data-testid="item-name-input" className="bg-input border-border rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Type</label>
                <Select value={itemForm.item_type} onValueChange={(v) => setItemForm({ ...itemForm, item_type: v })}>
                  <SelectTrigger className="bg-input border-border rounded-sm" data-testid="item-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {["weapon","armor","potion","scroll","wondrous","misc"].map(t => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Rarity</label>
                <Select value={itemForm.rarity} onValueChange={(v) => setItemForm({ ...itemForm, rarity: v })}>
                  <SelectTrigger className="bg-input border-border rounded-sm" data-testid="item-rarity-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {["common","uncommon","rare","very_rare","legendary"].map(r => (
                      <SelectItem key={r} value={r} className="capitalize">{r.replace("_"," ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Properties</label>
              <Input value={itemForm.properties} onChange={(e) => setItemForm({ ...itemForm, properties: e.target.value })} placeholder="Deals 2d6 extra fire damage..." data-testid="item-properties-input" className="bg-input border-border rounded-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Description</label>
              <RichTextEditor value={itemForm.description} onChange={(v) => setItemForm({ ...itemForm, description: v })} placeholder="Describe this item..." />
            </div>
            <Button type="submit" data-testid="save-item-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              {editingItem ? "Save Changes" : "Create Item"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// ============ Small inline components ============
const StatPill = ({ label, value }) => (
  <div className="rounded-sm border border-border bg-card/60 p-2 text-center" data-testid={`stat-pill-${label.toLowerCase()}`}>
    <p className="text-lg font-medium text-foreground tabular-nums">{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{label}</p>
  </div>
);

const SectionContainer = ({ title, icon: Icon, count, onAdd, addLabel, addTestId, emptyText, children }) => {
  const childrenIsEmpty = !children || (Array.isArray(children) && children.length === 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-serif text-foreground flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" /> {title} <span className="text-xs text-muted-foreground tabular-nums">({count})</span>
        </h3>
        {onAdd && (
          <Button onClick={onAdd} variant="outline" size="sm" className="rounded-sm text-xs" data-testid={addTestId}>
            <Plus className="w-3 h-3 mr-1" /> {addLabel}
          </Button>
        )}
      </div>
      {count === 0 ? (
        <p className="text-sm text-muted-foreground italic">{emptyText}</p>
      ) : children}
    </div>
  );
};

export default LocationsPage;
